var installed = false;

function safeStringify(value) {
	// We want something better than `[object Object]`, but must be resilient to circular refs.
	var seen = (typeof WeakSet !== 'undefined') ? new WeakSet() : null;
	try {
		var json = JSON.stringify(value, function (_key, val) {
			if (typeof val === 'bigint') {
				return String(val);
			}
			if (typeof val === 'function') {
				return '[Function]';
			}
			if (!val || typeof val !== 'object') {
				return val;
			}
			if (seen) {
				if (seen.has(val)) {
					return '[Circular]';
				}
				seen.add(val);
			}
			return val;
		});
		return (json === undefined) ? String(value) : json;
	} catch (e) {
		try {
			return String(value);
		} catch (e2) {
			return '[Unserializable]';
		}
	}
}

function formatConsoleArg(arg) {
	if (arg instanceof Error) {
		return arg.message || String(arg);
	}
	if (typeof arg === 'string') {
		return arg;
	}
	if (arg === null || arg === undefined) {
		return String(arg);
	}
	if (typeof arg === 'object') {
		// Common patterns: axios-like errors or plain objects with message fields.
		var msg = arg.message || arg.errorMessage || arg.errorMessageText;
		if (typeof msg === 'string' && msg) {
			return msg;
		}
		return safeStringify(arg);
	}
	return String(arg);
}

export function isBrowserExtensionNoise(error, context) {
	var parts = [];
	if (error !== undefined && error !== null) {
		parts.push(formatConsoleArg(error));
		if (error && typeof error === 'object') {
			parts.push(error.stack, error.filename, error.fileName, error.sourceURL);
		}
	}
	if (context) {
		parts.push(context.filename, context.url);
	}

	var text = parts.filter(Boolean).join(' ');
	return /(?:chrome|moz|edge|safari-web)-extension:\/\//i.test(text)
		|| /Attempting to use a disconnected port object/i.test(text)
		|| /The message port closed before a response was received/i.test(text)
		|| /Could not establish connection\. Receiving end does not exist/i.test(text)
		|| /Extension context invalidated/i.test(text)
		|| /message channel closed before a response was received/i.test(text);
}

function isCanceledErrorLike(error) {
	if (!error) return false;

	function isAbortLikeText(value) {
		return typeof value === 'string'
			&& (
				value.indexOf('AbortError') !== -1
				|| value.indexOf('ERR_CANCELED') !== -1
				|| /\bRequest aborted\b/i.test(value)
			);
	}

	// Some libs reject/callback with a string.
	if (typeof error === 'string') {
		return isAbortLikeText(error);
	}

	if (typeof error !== 'object') return false;

	var msg = error.message || error.errorMessage || error.errorMessageText;
	if (isAbortLikeText(msg)) return true;

	// axios cancel token / AbortController
	if (error.__CANCEL__) return true;
	if (error.code === 'ERR_CANCELED') return true;
	if (error.name === 'AbortError' || error.name === 'CanceledError') return true;

	return false;
}

function shouldIncludeResponseBodies() {
	try {
		return Boolean(typeof window !== 'undefined' && window.__RUM_CONFIG__ && window.__RUM_CONFIG__.includeResponseBodies);
	} catch (e) {
		return false;
	}
}

function sanitizeResponseData(data) {
	if (!data || typeof data !== 'object') {
		return data;
	}

	var out = {};
	var keys = [
		'success',
		'errorCode',
		'errorMessage',
		'errorMessageText',
		'message',
		'status',
		'sessionId',
		'transactionId',
		'timestamp',
	];
	keys.forEach(function (k) {
		if (data[k] !== undefined) {
			out[k] = data[k];
		}
	});
	if (data.errors !== undefined) {
		out.errors = data.errors;
	}
	return out;
}

/**
 * @param {Object} options
 * @param {Object} options.reporter - обов'язково; має методи captureError, captureMessage
 * @param {Object} [options.errorReporter] - alias для `reporter` (історична сумісність)
 * @param {boolean} [options.captureConsole=true] - перехоплювати console.error
 * @param {any} [options.axios] - інстанс axios (або window.axios)
 */
export function installErrorHandlers(options) {
	options = options || {};
	var reporter = options.reporter || options.errorReporter;
	var captureConsole = (typeof options.captureConsole === 'boolean') ? options.captureConsole : true;
	var axios = options.axios;

	if (installed) return;
	if (!reporter || typeof reporter.captureError !== 'function') return;
	installed = true;

	// 1) window.onerror
	function onError(event) {
		try {
			var err = (event && event.error) ? event.error : ((event && event.message) ? event.message : 'Unknown window.onerror');
			if (isCanceledErrorLike(err) || isBrowserExtensionNoise(err, {
				filename: event && event.filename,
			})) {
				return;
			}
			reporter.captureError(err, {
				source: 'window.error',
				filename: event && event.filename,
				lineno: event && event.lineno,
				colno: event && event.colno
			});
		} catch (e) {}
	}
	if (typeof window !== 'undefined' && window.addEventListener) {
		window.addEventListener('error', onError);
	}

	// 2) unhandledrejection
	function onRejection(event) {
		try {
			var reason = event ? event.reason : null;
			if (isCanceledErrorLike(reason) || isBrowserExtensionNoise(reason)) {
				return;
			}
			reporter.captureError(reason || 'Unhandled Promise rejection', { source: 'unhandledrejection' });
		} catch (e) {}
	}
	if (typeof window !== 'undefined' && window.addEventListener) {
		window.addEventListener('unhandledrejection', onRejection);
	}

	// 3) Resource loading errors (img/script/link) — capture=true
	function onResourceError(event) {
		try {
			var target = event ? event.target : null;
			if (!target) return;
			var tag = target.tagName && target.tagName.toLowerCase ? target.tagName.toLowerCase() : '';
			if (tag !== 'img' && tag !== 'script' && tag !== 'link') return;
			var url = target.currentSrc || target.src || (tag === 'link' ? target.href : undefined);
			if (isBrowserExtensionNoise(null, { url: url })) return;
			reporter.captureMessage('resource-load-error', { source: 'resource', tag: tag, url: url });
		} catch (e) {}
	}
	if (typeof window !== 'undefined' && window.addEventListener) {
		window.addEventListener('error', onResourceError, true);
	}

	// 4) console.error hijack
	if (captureConsole && typeof window !== 'undefined' && window.console && window.console.error) {
		var origConsoleError = window.console.error.bind(window.console);
		window.console.error = function () {
			try {
				var args = Array.prototype.slice.call(arguments);
				var consoleMessage = args.map(formatConsoleArg).join(' ');
				if (isBrowserExtensionNoise(consoleMessage)) {
					return origConsoleError.apply(null, arguments);
				}
				// Abort/cancel is expected (navigation/unmount), no need to spam backend logs.
				for (var i = 0; i < args.length; i++) {
					if (isCanceledErrorLike(args[i])) {
						return origConsoleError.apply(null, arguments);
					}
				}
				var first = args[0];
				if (!(first instanceof Error)) {
					var msg = consoleMessage;
					if (msg && msg.length > 4000) {
						msg = msg.slice(0, 4000) + '...';
					}
					reporter.captureMessage(msg, { source: 'console.error' });
				}
			} catch (e) {}
			return origConsoleError.apply(null, arguments);
		};
	}

	// 5) axios інтерсептор (якщо є)
	if (axios && axios.interceptors && axios.interceptors.response) {
		axios.interceptors.response.use(
			function (resp) { return resp; },
			function (error) {
				try {
					var cfg = error && error.config ? error.config : {};
					reporter.captureMessage('axios-error', {
						source: 'axios',
						url: cfg.url,
						method: cfg.method,
						status: error && error.response ? error.response.status : undefined,
						code: error ? error.code : undefined,
						data: (error && error.response)
							? (shouldIncludeResponseBodies() ? error.response.data : sanitizeResponseData(error.response.data))
							: undefined
					});
				} catch (e) {}
				return Promise.reject(error);
			}
		);
	}
}

export function reportError(reporter, error, custom) {
	if (!reporter) return;
	try { reporter.captureError(error, (custom || {})); } catch (e) {}
}

export function reportMessage(reporter, message, custom) {
	if (!reporter) return;
	try { reporter.captureMessage(message, (custom || {})); } catch (e) {}
}

export default {
	installErrorHandlers: installErrorHandlers,
	reportError: reportError,
	reportMessage: reportMessage
};
